// Running a thread action against the server, and undoing it. Thread actions are reversible
// mailbox changes, so one press runs them (decided 2026-10-12, Chris); the server still binds the
// approval to the exact thread, its latest message and the delta, and verifies it right before
// Gmail is called. Undo cancels the work while it waits; once it ran, Undo runs the `reverse`
// request the server named (one more approval, one more receipt, its own Undo). Nothing here is
// optimistic about Gmail: the row hides locally with an undo toast, and the list follows the
// server's `thread` event.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ThreadActionRequest, ThreadActionResult, ThreadSummary } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { invalidateBoards } from "@web/lib/feedQueries";
import { inboxApi } from "@web/lib/inboxApi";
import { inboxKeys, invalidateThreadWork } from "@web/lib/inboxQueries";
import { dismissUndo, registerUndo } from "@web/lib/undoRegistry";
import { undoReversible, type ReversibleUndo } from "@web/lib/undoReversible";
import { hidesFrom, refusalWords, requestWords, returnsTo } from "../labels";
import type { ListSearch } from "../listSearch";
import {
  followThreadAction,
  hideThread,
  patchFollow,
  revealThread,
  threadActionState,
  unhideThread,
  unrevealThread,
  type FollowedAction,
} from "./threadActionStore";

export function short(subject: string | null | undefined): string {
  const title = subject?.trim() || "(no subject)";
  return title.length > 48 ? `${title.slice(0, 45)}…` : title;
}

function explainFailure(error: unknown): string {
  if (isApiError(error, "invalid_request")) {
    const body = error.body as { reason?: unknown } | null;
    const reason = body && typeof body.reason === "string" ? body.reason : null;
    return refusalWords(reason, error.serverMessage);
  }
  if (isApiError(error) && error.status === 401) return "Sign in again to change mail.";
  if (isApiError(error) && error.status === 404) return "This thread isn't here anymore.";
  if (isApiError(error) && error.status === 409)
    return error.serverMessage ?? "This thread changed in the meantime. Look again.";
  if (isApiError(error, "not_available")) return "Thread actions aren't wired up yet.";
  return "Couldn't start that just now. Nothing changed in Gmail.";
}

/** Put the thread back: the reverse request runs at once, followed and undoable like any other. */
export function runReverse(
  queryClient: QueryClient,
  follow: FollowedAction,
): Promise<ThreadActionResult | null> {
  return runThreadAction(queryClient, follow.thread, follow.reverse, {
    search: follow.search,
    undoOf: follow.workId,
  });
}

/** Undo from the toast or `u`: cancel while queued; once it ran, run the reverse. */
async function undoFollowed(
  queryClient: QueryClient,
  follow: FollowedAction,
): Promise<ReversibleUndo> {
  try {
    const how = await undoReversible({
      cancel: () => inboxApi.cancelWork(follow.workId),
      reverse: () => runReverse(queryClient, follow),
    });
    if (how === "cancelled") unhideThread(follow.threadKey, follow.workId);
    return how;
  } finally {
    invalidateThreadWork(queryClient);
    invalidateBoards(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["work"] });
  }
}

export interface RunOptions {
  /** The list on screen: an action that takes the thread out of it hides the row at once. */
  search: ListSearch;
  /** The work this run undoes, if any: its hidden row comes back. */
  undoOf?: string | null;
}

/**
 * One press: ask for the approval, follow the work, offer Undo. Returns null (after a toast)
 * when the server refused; nothing changed in Gmail then.
 */
export async function runThreadAction(
  queryClient: QueryClient,
  thread: ThreadSummary,
  request: ThreadActionRequest,
  options: RunOptions,
): Promise<ThreadActionResult | null> {
  let result: ThreadActionResult;
  try {
    result = await inboxApi.runAction(thread.key, request);
  } catch (error) {
    toast(explainFailure(error));
    return null;
  }
  const words = requestWords(request);
  const follow: FollowedAction = {
    workId: result.work.id,
    threadKey: thread.key,
    thread: result.thread,
    request,
    reverse: result.reverse,
    search: options.search,
    startedAt: Date.now(),
    announced: null,
    undoToastId: null,
    dismissed: false,
  };
  followThreadAction(follow);
  if (options.undoOf) unhideThread(thread.key, options.undoOf);
  if (hidesFrom(request, options.search)) hideThread(thread.key, result.work.id);
  if (returnsTo(request, options.search)) revealThread(thread.key);
  queryClient.setQueryData(inboxKeys.work(result.work.id), result.work);
  invalidateThreadWork(queryClient);
  invalidateBoards(queryClient);
  void queryClient.invalidateQueries({ queryKey: ["work"] });
  if (result.deduped) {
    toast(`Already ${words.doing.toLowerCase()} this thread.`);
    return result;
  }
  const undoToast = registerUndo({
    message: `${words.doing}… ${short(thread.subject)}`,
    undo: () => undoFollowed(queryClient, follow),
    // The reverse announces itself with its own toast (and its own Undo).
    doneMessage: (how) => (how === "cancelled" ? "Cancelled. Nothing changed in Gmail." : null),
  });
  patchFollow(result.work.id, { announced: "queued", undoToastId: undoToast });
  return result;
}

/** The toast offered on a finished action: Undo runs the reverse. */
export function announceDone(queryClient: QueryClient, follow: FollowedAction): void {
  const words = requestWords(follow.request);
  registerUndo({
    message: `${words.done} · ${short(follow.thread.subject)}`,
    undo: () => runReverse(queryClient, follow),
    doneMessage: () => null,
  });
}

/** The work did not run (cancelled, stale): the row comes back and the queued toast goes. */
export function announceNotRun(follow: FollowedAction): void {
  if (threadActionState().hidden.get(follow.threadKey) === follow.workId)
    unhideThread(follow.threadKey, follow.workId);
  else unrevealThread(follow.threadKey);
  if (follow.undoToastId) dismissUndo(follow.undoToastId);
}

/** The work settled one way or another: the queued toast has served. */
export function dropQueuedToast(follow: FollowedAction): void {
  if (follow.undoToastId) {
    dismissUndo(follow.undoToastId);
    patchFollow(follow.workId, { undoToastId: null });
  }
}
